import React, { createContext, useContext, useState, useEffect } from 'react';

export interface ConsentState {
  analytics: boolean;
  marketing: boolean;
}

interface ConsentContextType {
  consentDecision: boolean; // Has decided?
  consentState: ConsentState;
  showBanner: boolean;
  showSettings: boolean;
  openSettings: () => void;
  closeSettings: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  saveConsent: (choices: ConsentState) => void;
  enableBookingConsent: () => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

const CONSENT_KEY = 'pstax_consent';

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consentDecision, setConsentDecision] = useState<boolean>(false);
  const [consentState, setConsentState] = useState<ConsentState>({
    analytics: false,
    marketing: false,
  });
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);

  // Read initial consent from localStorage on load
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CONSENT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setConsentState({
          analytics: !!parsed.analytics,
          marketing: !!parsed.marketing,
        });
        setConsentDecision(true);
        // Explicitly trigger a silent gtag update to ensure consistency on reload
        updateGtagConsent(parsed);
        updateClarityConsent(!!parsed.analytics);
      } else {
        setShowBanner(true);
        // No decision yet — deny by default, mirroring the Consent Mode default above.
        updateClarityConsent(false);
      }
    } catch (e) {
      console.error('Error loading initial consent', e);
      setShowBanner(true);
    }
  }, []);

  const updateGtagConsent = (choices: ConsentState) => {
    const g = (window as any).gtag;
    if (typeof g === 'function') {
      g('consent', 'update', {
        analytics_storage: choices.analytics ? 'granted' : 'denied',
        ad_storage: choices.marketing ? 'granted' : 'denied',
        ad_user_data: choices.marketing ? 'granted' : 'denied',
        ad_personalization: choices.marketing ? 'granted' : 'denied',
        functionality_storage: 'granted',
        security_storage: 'granted',
      });
    }
  };

  // Microsoft Clarity doesn't understand Google's Consent Mode signals — it has
  // its own consent API. GTM (which loads Clarity) is deferred until idle, and
  // we don't control whether Clarity's own bootstrap snippet queues calls made
  // before it's ready, so retry briefly instead of assuming a single check is enough.
  const updateClarityConsent = (analyticsGranted: boolean) => {
    const tryCall = (attemptsLeft: number) => {
      const c = (window as any).clarity;
      if (typeof c === 'function') {
        c('consent', analyticsGranted);
      } else if (attemptsLeft > 0) {
        setTimeout(() => tryCall(attemptsLeft - 1), 250);
      }
    };
    tryCall(40); // ~10s total
  };

  const acceptAll = () => {
    const choices = { analytics: true, marketing: true };
    setConsentState(choices);
    setConsentDecision(true);
    setShowBanner(false);
    setShowSettings(false);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(choices));
    updateGtagConsent(choices);
    updateClarityConsent(choices.analytics);
  };

  const rejectAll = () => {
    const choices = { analytics: false, marketing: false };
    setConsentState(choices);
    setConsentDecision(true);
    setShowBanner(false);
    setShowSettings(false);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(choices));
    updateGtagConsent(choices);
    updateClarityConsent(choices.analytics);
  };

  const saveConsent = (choices: ConsentState) => {
    setConsentState(choices);
    setConsentDecision(true);
    setShowBanner(false);
    setShowSettings(false);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(choices));
    updateGtagConsent(choices);
    updateClarityConsent(choices.analytics);
  };

  const enableBookingConsent = () => {
    const choices = { analytics: true, marketing: true };
    setConsentState(choices);
    setConsentDecision(true);
    setShowBanner(false);
    setShowSettings(false);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(choices));
    updateGtagConsent(choices);
    updateClarityConsent(choices.analytics);
  };

  const openSettings = () => {
    setShowSettings(true);
    setShowBanner(true); // Always display settings form within banner context when requesting it
  };

  const closeSettings = () => {
    setShowSettings(false);
    // If user already has a decision, close the whole banner. Otherwise, keep banner open.
    if (consentDecision) {
      setShowBanner(false);
    }
  };

  return (
    <ConsentContext.Provider
      value={{
        consentDecision,
        consentState,
        showBanner,
        showSettings,
        openSettings,
        closeSettings,
        acceptAll,
        rejectAll,
        saveConsent,
        enableBookingConsent,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};
